/**
 * Wishlist: any [data-wishlist-toggle="<product id>"] button becomes a persistent
 * favorite toggle, stored in localStorage so it survives across pages/sessions
 * (no customer account or backend required). Buttons also carry
 * data-wishlist-handle so the saved products can be listed: the header heart
 * ([data-wishlist-open]) shows the count and opens a drawer that loads each saved
 * product from /products/<handle>.js. Saving or removing shows a short toast.
 */
(function () {
  var STORAGE_KEY = 'carzelo:wishlist';
  var HANDLES_KEY = 'carzelo:wishlist-handles';

  function read(key, fallback) {
    try {
      var raw = window.localStorage.getItem(key);
      return raw ? JSON.parse(raw) : fallback;
    } catch (e) {
      return fallback;
    }
  }

  function write(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* localStorage unavailable (private mode, etc.) — toggle still works for this view */
    }
  }

  function readIds() { return read(STORAGE_KEY, []); }
  function readHandles() { return read(HANDLES_KEY, {}); }

  function paint() {
    var ids = readIds();
    var handles = readHandles();
    var learned = false;
    document.querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
      var id = btn.getAttribute('data-wishlist-toggle');
      var active = ids.indexOf(id) !== -1;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', String(active));
      // Items saved before handles were stored pick theirs up the next time they're on screen.
      var handle = btn.getAttribute('data-wishlist-handle');
      if (active && handle && handles[id] !== handle) { handles[id] = handle; learned = true; }
    });
    if (learned) write(HANDLES_KEY, handles);
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = ids.length;
      el.hidden = ids.length === 0;
    });
  }

  // --- Toast ---
  var toastTimer;
  function toast(message, withLink) {
    var el = document.querySelector('.wishlist-toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'wishlist-toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.appendChild(el);
    }
    el.innerHTML = '';
    var text = document.createElement('span');
    text.textContent = message;
    el.appendChild(text);
    if (withLink) {
      var link = document.createElement('button');
      link.type = 'button';
      link.className = 'wishlist-toast__link';
      link.textContent = 'View';
      link.addEventListener('click', function () { el.classList.remove('is-visible'); openDrawer(); });
      el.appendChild(link);
    }
    // Next frame so the slide-in transition runs even when the toast was just created.
    window.requestAnimationFrame(function () { el.classList.add('is-visible'); });
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(function () { el.classList.remove('is-visible'); }, 3200);
  }

  // --- Drawer ---
  var drawer;
  var lastFocus;

  var CART_ICON = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="9" cy="21" r="1"/><circle cx="19" cy="21" r="1"/><path d="M2 3h2l2.6 12.6a2 2 0 0 0 2 1.6h8.8a2 2 0 0 0 2-1.6L21 7H6"/></svg>';

  function formatMoney(cents) {
    return '₹' + (cents / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 });
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function buildDrawer() {
    drawer = document.createElement('div');
    drawer.className = 'wishlist-drawer';
    drawer.hidden = true;
    drawer.innerHTML =
      '<div class="wishlist-drawer__backdrop" data-wishlist-close></div>' +
      '<aside class="wishlist-drawer__panel" role="dialog" aria-modal="true" aria-labelledby="WishlistDrawerTitle" tabindex="-1">' +
        '<div class="wishlist-drawer__head">' +
          '<h2 id="WishlistDrawerTitle">Your wishlist</h2>' +
          '<button type="button" class="wishlist-drawer__close" data-wishlist-close aria-label="Close wishlist">&times;</button>' +
        '</div>' +
        '<div class="wishlist-drawer__body" data-wishlist-list></div>' +
      '</aside>';
    document.body.appendChild(drawer);
    drawer.addEventListener('click', function (e) {
      if (e.target.closest('[data-wishlist-close]')) closeDrawer();
      var add = e.target.closest('[data-wishlist-add]');
      if (add && window.CarzeloCart) { addToCart(add); return; }
      var choose = e.target.closest('[data-wishlist-choose]');
      if (choose) {
        // Hand over to the quick-buy popup (quick-add.js listens for [data-quick-buy] clicks).
        var url = choose.getAttribute('data-wishlist-choose');
        closeDrawer();
        var proxy = document.createElement('button');
        proxy.type = 'button';
        proxy.hidden = true;
        proxy.setAttribute('data-quick-buy', url);
        document.body.appendChild(proxy);
        proxy.click();
        proxy.remove();
        return;
      }
      var remove = e.target.closest('[data-wishlist-remove]');
      if (remove) {
        setSaved(remove.getAttribute('data-wishlist-remove'), false);
        renderList();
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer && !drawer.hidden) closeDrawer();
    });
  }

  function renderList() {
    var list = drawer.querySelector('[data-wishlist-list]');
    var ids = readIds();
    var handles = readHandles();
    var known = ids.filter(function (id) { return handles[id]; });
    if (!known.length) {
      list.innerHTML =
        '<div class="wishlist-drawer__empty">' +
          '<p><strong>Nothing saved yet</strong></p>' +
          '<p>Tap the heart on any product to keep it here for later.</p>' +
          '<a class="btn" href="/collections/all">Browse products</a>' +
        '</div>';
      return;
    }
    list.innerHTML = '<p class="wishlist-drawer__loading">Loading…</p>';
    Promise.all(known.map(function (id) {
      // The wishlist-product section (unlike /products/<handle>.js) says whether a choice is needed first.
      return fetch('/products/' + encodeURIComponent(handles[id]) + '?section_id=wishlist-product', { headers: { Accept: 'text/html' } })
        .then(function (res) { return res.ok ? res.text() : null; })
        .then(function (html) {
          var node = html && new DOMParser().parseFromString(html, 'text/html').querySelector('[data-wishlist-product]');
          return node ? JSON.parse(node.textContent) : null;
        })
        .catch(function () { return null; })
        .then(function (product) { return { id: id, product: product }; });
    })).then(function (entries) {
      var html = entries.filter(function (e) { return e.product; }).map(function (e, i) {
        var p = e.product;
        var action;
        if (!p.available) {
          action = '<button type="button" class="wishlist-item__add" disabled>Sold out</button>';
        } else if (p.needs_choice) {
          action = '<button type="button" class="wishlist-item__add" data-wishlist-choose="' + escapeHtml(p.url) + '">' + CART_ICON + '<span>Choose options</span></button>';
        } else {
          action = '<button type="button" class="wishlist-item__add" data-wishlist-add="' + escapeHtml(p.variant_id) + '">' + CART_ICON + '<span>Add to cart</span></button>';
        }
        var compare = p.compare_at_price > p.price ? ' <s>' + formatMoney(p.compare_at_price) + '</s>' : '';
        return '<div class="wishlist-item" style="--i:' + i + '">' +
          '<a class="wishlist-item__image" href="' + escapeHtml(p.url) + '">' + (p.image ? '<img src="' + escapeHtml(p.image) + '" alt="" width="72" height="72" loading="lazy">' : '') + '</a>' +
          '<div class="wishlist-item__info">' +
            '<a class="wishlist-item__title" href="' + escapeHtml(p.url) + '">' + escapeHtml(p.title) + '</a>' +
            '<span class="wishlist-item__price">' + formatMoney(p.price) + compare + '</span>' +
            '<div class="wishlist-item__actions">' + action +
              '<a class="wishlist-item__view" href="' + escapeHtml(p.url) + '">View</a>' +
            '</div>' +
          '</div>' +
          '<button type="button" class="wishlist-item__remove" data-wishlist-remove="' + escapeHtml(e.id) + '" aria-label="Remove ' + escapeHtml(p.title) + ' from wishlist">&times;</button>' +
        '</div>';
      }).join('');
      list.innerHTML = html || '<p class="wishlist-drawer__loading">These products are no longer available.</p>';
    });
  }

  function addToCart(btn) {
    var label = btn.querySelector('span');
    btn.disabled = true;
    label.textContent = 'Adding…';
    window.CarzeloCart.addItems([{ id: parseInt(btn.getAttribute('data-wishlist-add'), 10), quantity: 1 }]).then(function () {
      btn.classList.add('is-added');
      label.textContent = 'Added';
      var drawerOn = window.CarzeloCartDrawer && window.CarzeloCartDrawer.isEnabled();
      window.setTimeout(function () {
        if (drawerOn) closeDrawer();
        if (window.CarzeloAfterAdd) window.CarzeloAfterAdd();
        else if (window.CarzeloCartDrawer) window.CarzeloCartDrawer.refreshAfterAdd();
      }, drawerOn ? 450 : 0);
      window.setTimeout(function () { btn.classList.remove('is-added'); label.textContent = 'Add to cart'; btn.disabled = false; }, 1800);
    }).catch(function (err) {
      label.textContent = (err && err.message && /sold out|quantity/i.test(err.message)) ? 'Out of stock' : 'Try again';
      window.setTimeout(function () { label.textContent = 'Add to cart'; btn.disabled = false; }, 1800);
    });
  }

  function openDrawer() {
    if (!drawer) buildDrawer();
    lastFocus = document.activeElement;
    renderList();
    drawer.hidden = false;
    window.requestAnimationFrame(function () { drawer.classList.add('is-open'); });
    document.body.style.overflow = 'hidden';
    drawer.querySelector('.wishlist-drawer__panel').focus();
  }

  function closeDrawer() {
    drawer.classList.remove('is-open');
    document.body.style.overflow = '';
    window.setTimeout(function () { if (!drawer.classList.contains('is-open')) drawer.hidden = true; }, 260);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function setSaved(id, saved, handle) {
    var ids = readIds();
    var handles = readHandles();
    var index = ids.indexOf(id);
    if (saved && index === -1) ids.push(id);
    if (!saved && index !== -1) ids.splice(index, 1);
    if (saved && handle) handles[id] = handle;
    if (!saved) delete handles[id];
    write(STORAGE_KEY, ids);
    write(HANDLES_KEY, handles);
    paint();
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { ids: ids } }));
  }

  document.addEventListener('click', function (event) {
    if (event.target.closest('[data-wishlist-open]')) {
      event.preventDefault();
      openDrawer();
      return;
    }
    var btn = event.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    event.preventDefault();
    var id = btn.getAttribute('data-wishlist-toggle');
    var saved = readIds().indexOf(id) === -1;
    setSaved(id, saved, btn.getAttribute('data-wishlist-handle'));
    btn.classList.remove('is-bumped');
    void btn.offsetWidth;
    if (saved) btn.classList.add('is-bumped');
    toast(saved ? 'Saved to your wishlist' : 'Removed from your wishlist', saved);
  });

  document.addEventListener('DOMContentLoaded', paint);
})();
